import React from "react";
import { useSelector } from "react-redux";
import "./Dashboard.css";
import { PageHeader } from "CommonComponent";
import { DashboardCalendar, SummaryCard, UpcomingPefs } from "./component";
import { getVisibleDashboardSections } from "./dashboardMockData";
import { useDashboardPafSummary } from "./hooks/useDashboardPafSummary";
import { formatUpcomingPafDate } from "./utils/dashboardAppraisalSummary";

const getUserDisplayName = (user) => {
  if (!user || typeof user !== "object") return "";

  const fullName = [user.firstname, user.middlename, user.lastname]
    .filter(Boolean)
    .join(" ")
    .trim();

  const candidates = [fullName, user.full_name, user.name]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  const nonEmailName = candidates.find((value) => !value.includes("@"));
  if (nonEmailName) return nonEmailName;

  const email = String(user.email || candidates[0] || "");
  if (!email.includes("@")) return email;

  const localPart = email.split("@")[0] || "";
  const fromEmail = localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

  return fromEmail || "User";
};

const SummarySkeleton = () => (
  <section className="dashboard-summary" aria-busy="true" aria-label="Loading PAF status summary">
    {[0, 1, 2, 3].map((key) => (
      <article className="dashboard-summary-card dashboard-summary-card--skeleton" key={key}>
        <div className="dashboard-summary-card__icon dashboard-summary-card__skeleton-block" />
        <div className="dashboard-summary-card__content">
          <span className="dashboard-summary-card__skeleton-line dashboard-summary-card__skeleton-line--short" />
          <span className="dashboard-summary-card__skeleton-line dashboard-summary-card__skeleton-line--value" />
          <span className="dashboard-summary-card__skeleton-line dashboard-summary-card__skeleton-line--caption" />
        </div>
      </article>
    ))}
  </section>
);

const Dashboard = () => {
  const user = useSelector((state) => state.session?.user);
  const displayName = getUserDisplayName(user);
  const {
    summaryItems,
    reportingPeriod,
    today,
    events,
    upcoming,
    isLoading,
    error,
    refetch,
  } = useDashboardPafSummary();

  const visibleSections = getVisibleDashboardSections();
  const isVisible = (sectionId) =>
    visibleSections.some((section) => section.id === sectionId);
  const upcomingItems = upcoming.map((item) => ({
    ...item,
    due: formatUpcomingPafDate(item.date, today),
    cycle: item.frequency || item.designation || "",
  }));

  return (
    <main className="dashboard-page">
      <header className="dashboard-page__welcome">
        <PageHeader title="Dashboard" />
        <p>Welcome Back! {displayName}</p>
      </header>

      {isVisible("summary") && (
        <>
          {error ? (
            <div
              className="alert alert-danger d-flex justify-content-between align-items-center dashboard-summary-error"
              role="alert"
            >
              <span>{error}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
                Retry
              </button>
            </div>
          ) : null}

          {isLoading ? (
            <SummarySkeleton />
          ) : (
            <section className="dashboard-summary" aria-label="PAF status summary">
              {summaryItems.map((item) => (
                <SummaryCard item={item} period={reportingPeriod} key={item.id} />
              ))}
            </section>
          )}
        </>
      )}

      {isVisible("schedule") && (
        <section className="dashboard-schedule" aria-label="PAF schedule">
          <DashboardCalendar events={events} today={today} />
          <UpcomingPefs items={upcomingItems} today={today} />
        </section>
      )}
    </main>
  );
};

export default Dashboard;
